import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Dismiss20Regular,
  DocumentRegular,
  ChevronDownRegular,
  ChevronUpRegular,
} from "@fluentui/react-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import styles from "./CitationPanel.module.css";
import {
  AuthorityBadge,
  type CitationMetadata,
} from "../AuthorityBadge/AuthorityBadge";

type CitationPanelProps = {
  activeCitation: [
    content: string,
    id: string,
    title: string,
    filepath: string,
    url: string,
    metadata: CitationMetadata | null,
  ];
  setIsCitationPanelOpen: (flag: boolean) => void;
};

function rewriteCitationUrl(markdownText: string) {
  return markdownText.replace(
    /\[([^\]]+)\]\(([^)]+)\)/,
    (match, title, url) => {
      try {
        const parsed = new URL(url);
        const blobStorageHost = "blob.core.windows.net";

        if (parsed.hostname.includes(blobStorageHost)) {
          const filename = parsed.pathname.split("/").pop();
          return `[${title}](/api/files/${filename})`;
        } else {
          return `[${title}](${parsed.href})`;
        }
      } catch {
        return match;
      }
    }
  );
}

function isExternalUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return !parsed.hostname.includes("blob.core.windows.net");
  } catch {
    return false;
  }
}

export const CitationPanel: React.FC<CitationPanelProps> = (props) => {
  const { activeCitation, setIsCitationPanelOpen } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const authorityLevel = activeCitation[5]?.authority_level?.toLowerCase();
  const isContentRestricted =
    authorityLevel === "guidance" || authorityLevel === "informal";

  const onClose = useCallback(() => {
    setIsCitationPanelOpen(false);
  }, [setIsCitationPanelOpen]);

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Click overlay to close
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Citation details"
    >
      <div className={styles.modal} ref={modalRef}>
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.title}>
            <DocumentRegular className={styles.titleIcon} />
            Citations
          </h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close citation details"
          >
            <Dismiss20Regular />
          </button>
        </div>

        {/* Document name & title section */}
        <div className={styles.documentSection}>
          {activeCitation[5]?.regulation_title && (
            <h5 className={styles.citationTitle}>
              {activeCitation[5].regulation_title}
            </h5>
          )}
          <span className={styles.documentName}>{activeCitation[2]}</span>
        </div>

        {/* Authority badge & source link */}
        <div className={styles.metaSection}>
          <AuthorityBadge metadata={activeCitation[5] ?? undefined} />
          {activeCitation[4] && isExternalUrl(activeCitation[4]) && (
            <div className={styles.sourceLink}>
              <a
                href={activeCitation[4]}
                target="_blank"
                rel="noopener noreferrer"
              >
                View original document
              </a>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        {!isContentRestricted && (
          <div className={styles.disclaimer}>
            Tables, images, and other special formatting not shown in this preview.
          </div>
        )}

        {/* Scrollable content area */}
        <div className={styles.content}>
          {isContentRestricted ? (
            <div className={styles.contentUnavailable}>
              <p>Direct citations are not available for this source type.</p>
            </div>
          ) : (
            <div className={styles.citationContentWrapper}>
              <div
                className={`citation-panel ${isExpanded ? "" : styles.citationTruncated}`}
              >
                <ReactMarkdown
                  children={rewriteCitationUrl(activeCitation[0])}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    a: ({ node, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer" />
                    ),
                  }}
                />
              </div>
              {!isExpanded && <div className={styles.citationFadeOut} />}
              <button
                className={styles.expandButton}
                onClick={() => setIsExpanded(!isExpanded)}
                aria-label={isExpanded ? "Show less" : "Show more"}
              >
                {isExpanded ? (
                  <>
                    <ChevronUpRegular /> Show less
                  </>
                ) : (
                  <>
                    <ChevronDownRegular /> Show more
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.closeFooterButton}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
